import { set } from '@/utils/reactive-object.ts';
import i18n from '@/plugins/i18n.ts';
import { DATA, FIELD, FEATURE } from '@/components/payments/components/service-plan/components/features/constants';
import { ADD_ON_FIELD, ADD_ON_STATUS, FIELD as PLAN_FIELD, GROUP_FIELD } from '@/components/payments/components/service-plan/constants';
import ServicePlanFeatureEntity from '@/components/payments/components/service-plan/components/features/components/entity';
import ServicePlanFeatureLifespan from '@/components/payments/components/service-plan/components/features/components/lifespan';
import ServicePlanFeatureRecord from '@/components/payments/components/service-plan/components/features/components/record';
import ServicePlanFeatureRun from '@/components/payments/components/service-plan/components/features/components/run';
import ServicePlanFeatureSchedule from '@/components/payments/components/service-plan/components/features/components/schedule';
import ServicePlanFeatureScope from '@/components/payments/components/service-plan/components/features/components/scope';
import ServicePlanFeatureAssetEstimateCreate
    from '@/components/payments/components/service-plan/components/features/components/product/asset-estimate/create';
import ServicePlanFeatureAssetEstimateRatesManagement
    from '@/components/payments/components/service-plan/components/features/components/product/asset-estimate/rates-management';
import ServicePlanFeatureAssetEstimateRecordView
    from '@/components/payments/components/service-plan/components/features/components/product/asset-estimate/record-view';
import ServicePlanFeatureAssetEstimateRecordState
    from '@/components/payments/components/service-plan/components/features/components/product/asset-estimate/record-state';
import ServicePlanFeatureAssetEstimateReport
    from '@/components/payments/components/service-plan/components/features/components/product/asset-estimate/report';
import ServicePlanFeatureCalendahActiveCalendar
    from '@/components/payments/components/service-plan/components/features/components/product/calendah/active-calendar';
import ServicePlanFeatureCalendahShareScope
    from '@/components/payments/components/service-plan/components/features/components/product/calendah/share-scope.vue';
import ServicePlanFeatureCalendahAvailable
    from '@/components/payments/components/service-plan/components/features/components/product/calendah/available.vue';
import ServicePlanFeatureCalendahRange
    from '@/components/payments/components/service-plan/components/features/components/product/calendah/range.vue';
import ServicePlanFeatureCalendahView
    from '@/components/payments/components/service-plan/components/features/components/product/calendah/view.vue';
import ServicePlanFeatureCalendahProvider
    from '@/components/payments/components/service-plan/components/features/components/product/calendah/provider.vue';
import ServicePlanFeatureMigrationDelta
    from '@/components/payments/components/service-plan/components/features/components/product/migration/delta.vue';
import ServicePlanFeatureMigrationFilters
    from '@/components/payments/components/service-plan/components/features/components/product/migration/filters.vue';
import ServicePlanFeatureMigrationUpdateMatched
    from '@/components/payments/components/service-plan/components/features/components/product/migration/update-matched.vue';
import ServicePlanFeatureMigrationUpdateProperties
    from '@/components/payments/components/service-plan/components/features/components/product/migration/update-properties.vue';

export function getFeature(plan, product, feature) {
    return plan[PLAN_FIELD.OPTIONS]?.[product]?.[GROUP_FIELD.FEATURES]?.[feature];
}

export function getVisibleFeatures(features) {
    return Object.fromEntries(Object.entries(features).filter(([, feature]) => !feature.internal));
}

export function getFeatureComponent(feature) {
    return {
        [FEATURE.BACKUP_ENTITY]: ServicePlanFeatureEntity,
        [FEATURE.BACKUP_LIFESPAN]: ServicePlanFeatureLifespan,
        [FEATURE.BACKUP_RECORD]: ServicePlanFeatureRecord,
        [FEATURE.BACKUP_RUN]: ServicePlanFeatureRun,
        [FEATURE.BACKUP_SCHEDULE]: ServicePlanFeatureSchedule,

        [FEATURE.MIGRATION_DELTA]: ServicePlanFeatureMigrationDelta,
        [FEATURE.MIGRATION_FILTERS]: ServicePlanFeatureMigrationFilters,
        [FEATURE.MIGRATION_RECORD]: ServicePlanFeatureRecord,
        [FEATURE.MIGRATION_RUN]: ServicePlanFeatureRun,
        [FEATURE.MIGRATION_SCOPE]: ServicePlanFeatureScope,
        [FEATURE.MIGRATION_UPDATE_MATCHED]: ServicePlanFeatureMigrationUpdateMatched,
        [FEATURE.MIGRATION_UPDATE_PROPERTIES]: ServicePlanFeatureMigrationUpdateProperties,

        [FEATURE.CALENDAH_AVAILABLE]: ServicePlanFeatureCalendahAvailable,
        [FEATURE.CALENDAH_ACTIVE_CALENDAR]: ServicePlanFeatureCalendahActiveCalendar,
        [FEATURE.CALENDAH_SHARE_SCOPE]: ServicePlanFeatureCalendahShareScope,
        [FEATURE.CALENDAH_RANGE]: ServicePlanFeatureCalendahRange,
        [FEATURE.CALENDAH_VIEW]: ServicePlanFeatureCalendahView,
        [FEATURE.CALENDAH_PROVIDER]: ServicePlanFeatureCalendahProvider,

        [FEATURE.ASSET_ESTIMATE_CREATE]: ServicePlanFeatureAssetEstimateCreate,
        [FEATURE.ASSET_ESTIMATE_RECORD_VIEW]: ServicePlanFeatureAssetEstimateRecordView,
        [FEATURE.ASSET_ESTIMATE_RECORD_STATE]: ServicePlanFeatureAssetEstimateRecordState,
        [FEATURE.ASSET_ESTIMATE_REPORT]: ServicePlanFeatureAssetEstimateReport,
        [FEATURE.ASSET_ESTIMATE_RATES_MANAGEMENT]: ServicePlanFeatureAssetEstimateRatesManagement,
    }[feature] ?? null;
}

export function getFeatureValue(feature) {
    return feature?.[FIELD.DATA]?.[DATA.VALUE];
}

function sumActiveAddOns(feature, field) {
    return (feature?.[FIELD.ADD_ONS] ?? [])
        .filter(addOn => addOn[ADD_ON_FIELD.STATUS] === ADD_ON_STATUS.ACTIVE)
        .reduce((sum, addOn) => sum + (addOn[field] ?? 0), 0);
}

/**
 * The value of the plan with the records of the active add-ons
 */
export function getFeatureLimit(feature) {
    const value = getFeatureValue(feature);

    if (!value) return value;

    return value + sumActiveAddOns(feature, ADD_ON_FIELD.LIMIT);
}

/**
 * The used value of the plan with what is used of the active add-ons, to go with getFeatureLimit
 */
export function getFeatureLimitUsed(feature) {
    const used = getFeatureUsed(feature);

    if (!getFeatureValue(feature)) return used;

    return (used ?? 0) + sumActiveAddOns(feature, ADD_ON_FIELD.USED);
}

export function setFeatureValue(feature, value) {
    set(feature?.[FIELD.DATA], DATA.VALUE, value);
}

export function getFeatureScope(feature) {
    return feature?.[FIELD.DATA]?.[DATA.SCOPE];
}

export function getFeatureUsed(feature) {
    return feature?.[FIELD.DATA]?.[DATA.VALUE_USED];
}

export function getFeatureExclude(feature) {
    return feature?.[FIELD.DATA]?.[DATA.EXCLUDE] ?? [];
}

export function getFeatureUnit(feature) {
    return feature?.[FIELD.DATA]?.[DATA.VALUE_UNIT];
}

export function getFeaturePrice(feature) {
    return feature?.[FIELD.PRICE];
}

export function getFeatureCount(feature) {
    return feature?.[FIELD.DATA]?.[DATA.COUNT];
}

export function getFeatureFormat(feature) {
    return feature?.[FIELD.DATA]?.[DATA.FORMAT];
}

export function getFeatureExhaustion(feature, params = {}) {
    return {
        [FEATURE.MIGRATION_RECORD]: getFeatureRecordExhaustion,
        [FEATURE.MIGRATION_RUN]: getFeatureRunExhaustion,
        [FEATURE.MIGRATION_SCOPE]: getFeatureScopeExhaustion,
    }[feature[FIELD.NAME]]?.(feature, params) ?? getFeatureExhaustionResult();
}

export function getFeatureRecordExhaustion(feature) {
    return getFeatureLimitExhaustion(
        feature,
        i18n.t('payment.servicePlan.feature.record.exhausted'),
        getFeatureLimit(feature),
        getFeatureLimitUsed(feature),
    );
}

export function getFeatureRunExhaustion(feature) {
    return getFeatureLimitExhaustion(
        feature,
        i18n.t('payment.servicePlan.feature.run.exhausted'),
    );
}

export function getFeatureScopeExhaustion(feature, params = {}) {
    const count = getFeatureCount(feature);

    if (!count) return getFeatureExhaustionResult();

    const value = getFeatureValue(feature);
    const list = getFeatureValueList(value);
    const migrationId = params.migrationId;

    if (migrationId && list.some(id => String(id) === String(migrationId))) {
        return getFeatureExhaustionResult();
    }

    if (list.length < count) return getFeatureExhaustionResult();

    return getFeatureExhaustionResult(
        true,
        i18n.t('payment.servicePlan.feature.scope.exhausted'),
    );
}

function getFeatureLimitExhaustion(feature, message, value = getFeatureValue(feature), used = getFeatureUsed(feature)) {
    if (!value) return getFeatureExhaustionResult();

    if (!used || used < value) return getFeatureExhaustionResult();

    return getFeatureExhaustionResult(
        true,
        message,
    );
}

function getFeatureValueList(value) {
    if (Array.isArray(value)) return value;
    if (value) return [value];

    return [];
}

function getFeatureExhaustionResult(exhausted = false, message = null) {
    return {
        exhausted,
        message,
    };
}
